/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { Effect, WorkOrderTaskMessage } from "../../utils/Interfaces";
import { Box, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import { Cancel, CheckCircle, DateRange, Delete, Edit, Person } from "@mui/icons-material";
import { useDeleteWorkOrderTaskMessage, useEditWorkOrderTaskMessage } from "./workOrderHooks";
import { chatDateStylization, generateColorFromString } from "../../utils/Functions";
import { selectId } from "../auth/authSlice";
import { useSelector } from "react-redux";

export interface WorkOrderTaskMessageEditorProps {
    workOrderId: number;
    taskMessage: WorkOrderTaskMessage;

    additionalAction?: Effect;
}

const WorkOrderTaskMessageEditor = (props: WorkOrderTaskMessageEditorProps) => {
    const { workOrderId, taskMessage, additionalAction } = props;

    const [message, setMessage] = useState<string>(taskMessage.message);
    const myUserId: number = useSelector(selectId);

    const [editMessage, setEditMessage] = useState<boolean>(false);

    const { mutate: editWorkOrderTaskMessage, status: workOrderTaskMessageEditStatus } = useEditWorkOrderTaskMessage(
        workOrderId,
        taskMessage.id,
        additionalAction === undefined ? () => {} : additionalAction
    );
    const { mutate: deleteWorkOrderTaskMessage, status: workOrderTaskMessageDeletionStatus } = useDeleteWorkOrderTaskMessage(
        workOrderId,
        taskMessage.id,
        additionalAction === undefined ? () => {} : additionalAction
    );

    function handleEditTaskMessage() {
        editWorkOrderTaskMessage({ message: message });
    }

    function handleDeleteTaskMessage() {
        deleteWorkOrderTaskMessage({ message: message });
    }

    return (
        <Box sx={{ backgroundColor: generateColorFromString(taskMessage.user.username, 0.05) }}>
            <Stack direction="row" justifyContent="flex-start">
                <Box
                    sx={{
                        backgroundColor: generateColorFromString(taskMessage.user.username, 0.75),
                        width: "3px",
                    }}
                />
                <Stack direction="column" justifyContent="flex-start" sx={{ width: "100%", ml: 1, mt: 0.5 }}>
                    <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" justifyContent="flex-start">
                            <Person fontSize="small" sx={{ transform: "scale(0.75)" }} />
                            <Typography sx={{ ml: 1, mt: 0.2 }} variant="caption">
                                {taskMessage.user.first_name} {taskMessage.user.last_name}
                            </Typography>
                            &nbsp;&nbsp;
                            <DateRange fontSize="small" sx={{ transform: "scale(0.75)" }} />
                            <Typography sx={{ ml: 1, mt: 0.2 }} variant="caption">
                                {chatDateStylization(taskMessage.timestamp)}
                            </Typography>
                            {taskMessage.edited && (
                                <Typography sx={{ ml: 1, mt: 0.2 }} variant="caption" color="text.secondary">
                                    (edited)
                                </Typography>
                            )}
                        </Stack>
                        {!editMessage && (
                            <Stack direction="row" justifyContent="flex-end">
                                {workOrderTaskMessageDeletionStatus !== "pending" && (
                                    <Box>
                                        {myUserId === taskMessage.user.id && (
                                            <Tooltip title="Edit message">
                                                <IconButton onClick={() => setEditMessage(true)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {myUserId === taskMessage.user.id && (
                                            <Tooltip title="Delete message">
                                                <IconButton onClick={() => handleDeleteTaskMessage()}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                                {workOrderTaskMessageDeletionStatus === "pending" && (
                                    <CircularProgress size={16} sx={{ mt: 1.25, mr: 1, ml: 1.25, mb: 1.25 }} />
                                )}
                            </Stack>
                        )}
                        {editMessage && (
                            <Stack direction="row" justifyContent="flex-end">
                                {workOrderTaskMessageEditStatus !== "pending" && (
                                    <Box>
                                        {myUserId === taskMessage.user.id && (
                                            <Tooltip title="Discard changes">
                                                <IconButton
                                                    onClick={() => {
                                                        setEditMessage(false);
                                                        setMessage(taskMessage.message);
                                                    }}
                                                >
                                                    <Cancel fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {myUserId === taskMessage.user.id && (
                                            <Tooltip title={message.trim() === "" ? "Delete message" : "Save changes"}>
                                                <IconButton
                                                    onClick={() => handleEditTaskMessage()}
                                                    color={message.trim() === "" ? "default" : "primary"}
                                                >
                                                    {message.trim() === "" ? (
                                                        <Delete fontSize="small" />
                                                    ) : (
                                                        <CheckCircle fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                                {workOrderTaskMessageEditStatus === "pending" && (
                                    <CircularProgress size={16} sx={{ mt: 1.25, mr: 1, ml: 1.25, mb: 1.25 }} />
                                )}
                            </Stack>
                        )}
                    </Stack>
                    {!editMessage && (
                        <Typography variant="subtitle2" sx={{ mb: 1, ml: 0.5 }}>
                            {taskMessage.message}
                        </Typography>
                    )}
                    {editMessage && (
                        <TextField
                            sx={{ mb: 2 }}
                            fullWidth
                            multiline={true}
                            maxRows={4}
                            size="small"
                            value={message}
                            onChange={(event) => setMessage(event.target.value)}
                            InputProps={{
                                style: {
                                    borderRadius: 10,
                                },
                            }}
                        />
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default WorkOrderTaskMessageEditor;
